// src/services/coach-services.ts
import { kvBatch, kvGet } from "@/lib/storage/kv-client.ts";
import { BatchOperation } from "@/lib/storage/types.ts";

export interface CoachedAthlete {
  athleteId: string;
  access: "full" | "read-only";
  name: string; // Kept for backward compatibility, auto-synced with firstName/lastName/nickname
  firstName: string;
  lastName: string;
  nickname: string;
  isSignedUp: boolean;
}

export type PropertyType =
  | "text"
  | "date"
  | "label"
  | "multi-label"
  | "number"
  | "checkbox";

export interface PropertyTypeToValueMap {
  text: string;
  date: string; // ISO date string
  label: string;
  "multi-label": string[];
  number: number;
  checkbox: boolean;
}
export interface AthleteProperty {
  id: string;
  name: string;
  type: PropertyType;
  options?: string[];
  isRequired?: boolean;
  defaultValue?: PropertyTypeToValueMap[PropertyType];
}

// Property value (for athlete)
export interface AthletePropertyValue {
  propertyId: string;
  type: PropertyType;
  value: PropertyTypeToValueMap[PropertyType];
}

export interface TableSettings {
  columnOrder?: string[]; // Order of column IDs ["name", "property_1", "property_2"]
  columnVisibility?: Record<string, boolean>; // Which columns are visible
  columnWidths?: Record<string, number>; // Optional column widths
}

export interface CoachProfile {
  uid: string;
  email: string;
  role: "coach";
  name: string;
  firstName: string;
  lastName: string;
  nickname: string;
  personalisedMessage: string;
  // Note: athletes array removed - query athletes by coachId from athlete profiles instead
  athleteProperties: AthleteProperty[];
  settings: {
    theme?: "light" | "dark";
  };
  tableSettings?: TableSettings;
  createdAt: number;
  notionToken?: string;
  notionDatabaseId?: string;
}

export interface CoachUpdateParams {
  name?: string;
  firstName?: string;
  lastName?: string;
  nickname?: string;
  personalisedMessage?: string;
  email?: string;
  settings?: Partial<CoachProfile["settings"]>;
  tableSettings?: Partial<TableSettings>;
  // Note: athletes removed - managed via athlete profiles instead
  notionToken?: string;
  notionDatabaseId?: string;
  athleteProperties?: AthleteProperty[];
}

export class CoachManagementError extends Error {
  constructor(
    message: string,
    public code: "NOT_FOUND" | "UNAUTHORIZED" | "INVALID_OPERATION",
  ) {
    super(message);
    this.name = "CoachManagementError";
  }
}

export class CoachServices {
  static async createCoachProfile(
    uid: string,
    email: string,
    name: string,
  ): Promise<CoachProfile> {
    const coach: CoachProfile = {
      uid,
      email,
      role: "coach",
      name,
      firstName: "",
      lastName: "",
      nickname: name,
      personalisedMessage: "",
      athleteProperties: [],
      settings: {
        theme: "dark",
      },
      createdAt: Date.now(),
    };

    try {
      // Sync metadata for coach profile
      const syncMetadata = {
        userId: uid,
        type: "COACH_PROFILE" as const,
        entityId: `COACH_PROFILE-${uid}`,
        createdBy: uid,
      };

      const batchOps: BatchOperation[] = [
        {
          type: "set",
          key: `coach:${uid}`,
          value: coach,
          syncMetadata,
        },
      ];

      await kvBatch(batchOps);
      return coach;
    } catch (error) {
      console.error("Failed to create coach profile:", error);
      throw new CoachManagementError(
        "Failed to create coach profile",
        "INVALID_OPERATION",
      );
    }
  }

  static async getCoachProfile(uid?: string): Promise<CoachProfile> {
    try {
      if (!uid) {
        throw new CoachManagementError("No User Id Provided", "NOT_FOUND");
      }
      const coach = (await kvGet<CoachProfile>(`coach:${uid}`, {
        backgroundRefresh: true, // Load from cache immediately, refresh in background
      }))!;

      if (!coach) {
        throw new CoachManagementError("Coach profile not found", "NOT_FOUND");
      }

      return coach;
    } catch (error) {
      if (error instanceof CoachManagementError) {
        throw error;
      }
      console.error("Failed to get coach profile:", error);
      throw new CoachManagementError(
        "Failed to load coach profile",
        "NOT_FOUND",
      );
    }
  }

  static async updateCoachProfile(
    uid: string,
    params: CoachUpdateParams,
  ): Promise<CoachProfile> {
    const currentProfile = await this.getCoachProfile(uid);

    const updatedProfile: CoachProfile = {
      ...currentProfile,
      ...params,
      settings: {
        ...currentProfile.settings,
        ...(params.settings ?? {}),
      },
      tableSettings: params.tableSettings
        ? {
            ...(currentProfile.tableSettings ?? {}),
            ...params.tableSettings,
          }
        : currentProfile.tableSettings,
    };

    try {
      // Sync metadata for coach profile update
      const syncMetadata = {
        userId: uid,
        type: "COACH_PROFILE" as const,
        entityId: `COACH_PROFILE-${uid}`,
        createdBy: uid,
      };

      await kvBatch([
        {
          type: "set",
          key: `coach:${uid}`,
          value: updatedProfile,
          syncMetadata,
        },
      ]);
      return updatedProfile;
    } catch (error) {
      console.error("Failed to update coach profile:", error);
      throw new CoachManagementError(
        "Failed to update coach profile",
        "INVALID_OPERATION",
      );
    }
  }
}
